import React from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { singleSearchSchema, type SingleSearchFormData } from '@/utils/searchValidation';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, AlertCircle, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SearchType } from '@/types';

interface SearchFormProps {
  onSubmit: (data: SingleSearchFormData) => void;
  isLoading?: boolean;
  className?: string;
  searchType: SearchType;
}

export const SearchForm: React.FC<SearchFormProps> = ({ onSubmit, isLoading = false, className, searchType }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<SingleSearchFormData>({
    resolver: zodResolver(singleSearchSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      amountToCollect: undefined,
      searchType: searchType,
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      addresses: [],
      phones: [],
      emails: [],
    },
  });

  // Field arrays for multiple entries (only for validate/scoring operations)
  const { fields: addressFields, append: appendAddress, remove: removeAddress } = useFieldArray({
    control,
    name: 'addresses',
  });

  const { fields: phoneFields, append: appendPhone, remove: removePhone } = useFieldArray({
    control,
    name: 'phones' as any, // phones is string[], not object[]
  });

  const { fields: emailFields, append: appendEmail, remove: removeEmail } = useFieldArray({
    control,
    name: 'emails' as any, // emails is string[], not object[]
  });

  // Update form's searchType when prop changes
  React.useEffect(() => {
    setValue('searchType', searchType);

    // Clear amountToCollect when switching to non-scoring operation
    if (searchType !== 'scoring' && searchType !== 'scoring_enrich') {
      setValue('amountToCollect', undefined);
    }
  }, [searchType, setValue]);

  const showAmountField = searchType === 'scoring' || searchType === 'scoring_enrich';
  const isMultiEntryOperation = searchType === 'validate';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn('space-y-3', className)}>
      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-2">
            First Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="firstName"
            type="text"
            placeholder="Enter first name"
            {...register('firstName')}
            className={cn(errors.firstName && 'border-red-500')}
            disabled={isLoading}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.firstName.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-2">
            Last Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="lastName"
            type="text"
            placeholder="Enter last name"
            {...register('lastName')}
            className={cn(errors.lastName && 'border-red-500')}
            disabled={isLoading}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.lastName.message}
            </p>
          )}
        </div>
      </div>

      {/* Contact Information - At least one required */}
      <div className="border-t border-slate-200 pt-6 mt-2">
        <h3 className="text-lg font-medium text-slate-900 mb-2">Contact Information</h3>
        <p className="text-sm text-slate-600 mb-4">
          Provide at least one: <span className="font-semibold">Phone</span>, <span className="font-semibold">Email</span>, or <span className="font-semibold">Address</span>
        </p>
      </div>

      {/* Address Fields */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="address" className="block text-sm font-medium text-slate-700">
            Street Address
          </label>
          {isMultiEntryOperation && addressFields.length < 4 && (
            <button
              type="button"
              onClick={() => appendAddress({ address: '', city: '', state: '', zip: '' })}
              disabled={isLoading}
              className="group relative inline-flex items-center gap-1 text-xs text-slate-600 hover:text-collectability-blue transition-colors"
              title="Add up to 5 addresses"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Add another</span>
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                Add up to 5 addresses
              </div>
            </button>
          )}
        </div>
        <Input
          id="address"
          type="text"
          placeholder="123 Main St"
          {...register('address')}
          className={cn(errors.address && 'border-red-500')}
          disabled={isLoading}
        />
        {errors.address && (
          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.address.message}
          </p>
        )}
      </div>

      {/* City, State, ZIP */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-slate-700 mb-2">
            City
          </label>
          <Input
            id="city"
            type="text"
            placeholder="Los Angeles"
            {...register('city')}
            className={cn(errors.city && 'border-red-500')}
            disabled={isLoading}
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.city.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="state" className="block text-sm font-medium text-slate-700 mb-2">
            State
          </label>
          <Input
            id="state"
            type="text"
            placeholder="CA"
            maxLength={2}
            {...register('state')}
            className={cn('uppercase', errors.state && 'border-red-500')}
            disabled={isLoading}
          />
          {errors.state && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.state.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="zip" className="block text-sm font-medium text-slate-700 mb-2">
            ZIP Code
          </label>
          <Input
            id="zip"
            type="text"
            placeholder="90210"
            maxLength={10}
            {...register('zip')}
            className={cn(errors.zip && 'border-red-500')}
            disabled={isLoading}
          />
          {errors.zip && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.zip.message}
            </p>
          )}
        </div>
      </div>

      {/* Additional Address Entries (Multi-Entry for Validate/Scoring) */}
      {isMultiEntryOperation && addressFields.length > 0 && (
        <div className="space-y-3 pt-2">
          {addressFields.map((field, index) => (
            <div key={field.id} className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50 relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-600">Address {index + 2}</span>
                <button
                  type="button"
                  onClick={() => removeAddress(index)}
                  disabled={isLoading}
                  className="text-red-600 hover:text-red-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div>
                <Input
                  type="text"
                  placeholder="123 Main St"
                  {...register(`addresses.${index}.address`)}
                  className={cn(errors.addresses?.[index]?.address && 'border-red-500')}
                  disabled={isLoading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  type="text"
                  placeholder="City"
                  {...register(`addresses.${index}.city`)}
                  disabled={isLoading}
                />
                <Input
                  type="text"
                  placeholder="State"
                  maxLength={2}
                  {...register(`addresses.${index}.state`)}
                  className="uppercase"
                  disabled={isLoading}
                />
                <Input
                  type="text"
                  placeholder="ZIP"
                  maxLength={10}
                  {...register(`addresses.${index}.zip`)}
                  disabled={isLoading}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Phone and Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="phone" className="block text-sm font-medium text-slate-700">
              Phone Number
            </label>
            {isMultiEntryOperation && phoneFields.length < 4 && (
              <button
                type="button"
                onClick={() => appendPhone('' as any)}
                disabled={isLoading}
                className="group relative inline-flex items-center gap-1 text-xs text-slate-600 hover:text-collectability-blue transition-colors"
                title="Add up to 5 phone numbers"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Add another</span>
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Add up to 5 phone numbers
                </div>
              </button>
            )}
          </div>
          <Input
            id="phone"
            type="tel"
            placeholder="5551234567 (10 digits)"
            maxLength={10}
            {...register('phone')}
            className={cn(errors.phone && 'border-red-500')}
            disabled={isLoading}
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.phone.message}
            </p>
          )}
          <p className="mt-1 text-xs text-slate-500">Enter 10 digits without dashes or spaces</p>

          {/* Additional Phone Entries */}
          {isMultiEntryOperation && phoneFields.length > 0 && (
            <div className="space-y-2 mt-3">
              {phoneFields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-2">
                  <Input
                    type="tel"
                    placeholder="5551234567"
                    maxLength={10}
                    {...register(`phones.${index}`)}
                    className={cn(errors.phones?.[index] && 'border-red-500')}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => removePhone(index)}
                    disabled={isLoading}
                    className="text-red-600 hover:text-red-700 transition-colors p-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Email Address <span className="text-slate-400 text-xs">(Optional)</span>
            </label>
            {isMultiEntryOperation && emailFields.length < 4 && (
              <button
                type="button"
                onClick={() => appendEmail('' as any)}
                disabled={isLoading}
                className="group relative inline-flex items-center gap-1 text-xs text-slate-600 hover:text-collectability-blue transition-colors"
                title="Add up to 5 email addresses"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Add another</span>
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Add up to 5 email addresses
                </div>
              </button>
            )}
          </div>
          <Input
            id="email"
            type="email"
            placeholder="email@example.com"
            {...register('email')}
            className={cn(errors.email && 'border-red-500')}
            disabled={isLoading}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.email.message}
            </p>
          )}

          {/* Additional Email Entries */}
          {isMultiEntryOperation && emailFields.length > 0 && (
            <div className="space-y-2 mt-3">
              {emailFields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-2">
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    {...register(`emails.${index}`)}
                    className={cn(errors.emails?.[index] && 'border-red-500')}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => removeEmail(index)}
                    disabled={isLoading}
                    className="text-red-600 hover:text-red-700 transition-colors p-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Amount to Collect - Only shown for scoring operations */}
      {showAmountField && (
        <div>
          <label htmlFor="amountToCollect" className="block text-sm font-medium text-slate-700 mb-2">
            Amount to Collect <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
            <Input
              id="amountToCollect"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              {...register('amountToCollect', { valueAsNumber: true })}
              className={cn('pl-8', errors.amountToCollect && 'border-red-500')}
              disabled={isLoading}
            />
          </div>
          {errors.amountToCollect && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.amountToCollect.message}
            </p>
          )}
          <p className="mt-1 text-xs text-slate-500">Enter the debt amount you're trying to collect</p>
        </div>
      )}

      {/* Search Type - Hidden input, controlled by parent */}
      <input type="hidden" {...register('searchType')} value={searchType} />

      {/* Submit Button */}
      <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            Processing...
          </>
        ) : (
          <>
            <Search className="w-5 h-5 mr-2" />
            Continue to Payment
          </>
        )}
      </Button>

      <p className="text-xs text-slate-500 text-center">
        By continuing, you agree to our Terms of Service and Privacy Policy
      </p>
    </form>
  );
};
